import {
  AnalyzeImagesDto,
  AnalyzeImagesResponseDto,
  CreateCustomInputSourceImageDto,
  DeleteSourceImagesResponseDto,
  GetImageAnalysisResultsDto,
  GetImageAnalysisResultsResponseDto,
  GetSourceImageDownloadUrlResponseDto,
  GetSourceImagesByExtIdsResponseDto,
  GetSourceImagesByOriginVideoExternalIdResponseDto,
  GetSourceImagesQuery,
  PagedSourceImagesResponse,
  SourceImageDto,
} from "@repo/api/source-image";
import { AmplifilesClient } from "./api-client";
import { AnalyticsApi } from "./analytics-api";

export class SourceImageApi {
  static async getSourceImagesByOriginVideoExternalId(
    teamExternalId: string,
    videoExternalId: string,
  ): Promise<GetSourceImagesByOriginVideoExternalIdResponseDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/video/${videoExternalId}/source-images`,
    );

    return GetSourceImagesByOriginVideoExternalIdResponseDto.parse(
      response.data,
    );
  }

  static async getPagedSourceImages(
    teamExternalId: string,
    query: GetSourceImagesQuery,
  ): Promise<PagedSourceImagesResponse> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/source-image`,
      { params: query },
    );

    return PagedSourceImagesResponse.parse(response.data);
  }

  static async getSourceImagesByExtIds(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<GetSourceImagesByExtIdsResponseDto> {
    if (externalIds.length === 0) {
      return {};
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/source-image-by-ext-ids`,
      { externalIds },
    );

    return GetSourceImagesByExtIdsResponseDto.parse(response.data);
  }

  static async uploadImage(
    teamExternalId: string,
    videoExternalId: string,
    image: File,
  ): Promise<SourceImageDto> {
    const formData = new FormData();
    formData.append("image", image);

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/video/${videoExternalId}/source-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    AnalyticsApi.logImageAdded();

    return SourceImageDto.parse(response.data);
  }

  static async analyzeImages(
    teamExternalId: string,
    data: AnalyzeImagesDto,
  ): Promise<AnalyzeImagesResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/analyze-images`,
      data,
    );

    return AnalyzeImagesResponseDto.parse(response.data);
  }

  static async getImageAnalysisResults(
    teamExternalId: string,
    data: GetImageAnalysisResultsDto,
  ): Promise<GetImageAnalysisResultsResponseDto> {
    AnalyticsApi.logAnimationsSuggested();

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/image-analysis-results`,
      data,
    );

    return GetImageAnalysisResultsResponseDto.parse(response.data);
  }

  static async deleteSourceImages(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<DeleteSourceImagesResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/source-images/delete`,
      { externalIds },
    );

    return DeleteSourceImagesResponseDto.parse(response.data);
  }

  static async createCustomInputSourceImage(
    teamExternalId: string,
    data: CreateCustomInputSourceImageDto,
  ): Promise<SourceImageDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/source-image/custom-prompt`,
      data,
    );

    return SourceImageDto.parse(response.data);
  }

  static async getDownloadUrl(
    teamExternalId: string,
    externalId: string,
  ): Promise<GetSourceImageDownloadUrlResponseDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/source-image/${externalId}/download-url`,
    );
    return GetSourceImageDownloadUrlResponseDto.parse(response.data);
  }
}
